import { aiModels } from "@/components/dashboard/settings/VoiceSettings"
import { findVoiceById } from "@/lib/default_voices"
import { getHandoffConditionByPrompt } from "@/lib/handoff"
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react"
import { CheckCircle, InfoCircle, NavArrowDown, PhoneXmark, Settings } from "iconoir-react"
import React from "react"

interface CallSettingsCardProps {
  call: any
}

export default function CallSettingsCard({ call }: CallSettingsCardProps): React.JSX.Element | null {
  const voicemailMessage = call.voicemail_message || "No voicemail message set, default message will be used."
  const voice = findVoiceById(call.voice_id)
  const voiceName = voice ? voice.name : "Unknown Voice"
  const aiModel = aiModels.find((model) => model.value === call.model_slug)?.displayName || "Unknown AI Model"

  return (
    <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
      <Disclosure defaultOpen={false}>
        {({ open }) => (
          <div>
            <DisclosureButton
              id="call-settings-disclosure-button"
              className="flex w-full justify-between items-center p-5 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Settings className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-base font-medium text-gray-900">Call Settings</h3>
              </div>
              <NavArrowDown
                className={`${
                  open ? "rotate-180 transform" : ""
                } h-5 w-5 text-blue-500 transition-transform duration-200`}
              />
            </DisclosureButton>
            <DisclosurePanel className="border-t border-gray-200 p-5">
              <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Voice Settings */}
                {call.task_slug !== "prank_call" && (
                  <div id="voice-settings-card" className="bg-blue-50 rounded-xl p-4 flex flex-col shadow-sm">
                    <div className="flex items-center mb-2">
                      <h3 className="text-sm font-semibold text-blue-800">Voice</h3>
                      <div className="ml-auto">
                        <InfoCircle className="text-blue-600" width={16} height={16} />
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 font-medium">{voiceName}</p>
                    <p className="text-xs text-gray-500 mt-1">The voice that will speak during this call</p>
                  </div>
                )}

                {/* AI Model Settings */}
                <div id="ai-settings-card" className="bg-purple-50 rounded-xl p-4 flex flex-col shadow-sm">
                  <div className="flex items-center mb-2">
                    <h3 className="text-sm font-semibold text-purple-800">AI Intelligence</h3>
                    <div className="ml-auto">
                      <InfoCircle className="text-purple-600" width={16} height={16} />
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 font-medium">{aiModel}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Determines how smart and responsive your AI caller will be
                  </p>
                </div>

                {/* Voicemail Settings */}
                <div id="voicemail-settings-card" className="bg-green-50 rounded-xl p-4 flex flex-col shadow-sm">
                  <div className="flex items-center mb-2">
                    <h3 className="text-sm font-semibold text-green-800">Voicemail</h3>
                    <div className="ml-auto">
                      {call.leave_voicemail_message ? (
                        <CheckCircle className="text-green-600" width={16} height={16} />
                      ) : (
                        <PhoneXmark className="text-red-500" width={16} height={16} />
                      )}
                    </div>
                  </div>
                  {call.leave_voicemail_message ? (
                    <>
                      <p className="text-sm text-gray-700 italic">{voicemailMessage}</p>
                      <p className="text-xs text-gray-500 mt-1">Voicemail will be left if no one answers</p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-700">Voicemail feature is disabled for this call</p>
                  )}
                </div>

                {/* Handoff Settings */}
                <div id="handoff-settings-card" className="bg-amber-50 rounded-xl p-4 flex flex-col shadow-sm">
                  <div className="flex items-center mb-2">
                    <h3 className="text-sm font-semibold text-amber-800">Live Handoff</h3>
                    <div className="ml-auto">
                      {call.handoff_condition ? (
                        <CheckCircle className="text-green-600" width={16} height={16} />
                      ) : (
                        <PhoneXmark className="text-red-500" width={16} height={16} />
                      )}
                    </div>
                  </div>
                  {call.handoff_condition ? (
                    <>
                      <p className="text-sm text-gray-700 italic">
                        {getHandoffConditionByPrompt(call.handoff_condition)?.title || call.handoff_condition}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Call will transfer to you automatically when this condition is met
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-700">Live handoff is disabled for this call</p>
                  )}
                </div>
              </div>
            </DisclosurePanel>
          </div>
        )}
      </Disclosure>
    </div>
  )
}
